import { defineComponent as _defineComponent } from 'vue'
import {
  ref, PropType, getCurrentInstance, onBeforeUnmount, onMounted,
} from 'vue';
import i18n from '@/plugins/i18n';
import httpClient from '@/api/http-client';
import { performSaveAction, performDispatchAction } from '@/services/vue-utils';
import HtmlTemplate from '@/grpc-api/model/html-template';
import store from '@/store/store';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import ReservationService from '@/grpc-api/model/reservation-service';
import { hostedWidgetURL } from '@/services/configuration';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import { InitApp, DestroyApp } from '@guestplan/email-editor';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import { cloneModel } from '@/model/model-utils';
import { TranslateResult } from 'vue-i18n';
import { useLanguages } from '../settings/helpers/settings-values';


export default /*#__PURE__*/_defineComponent({
  __name: 'NewsletterTemplateEditorDialog',
  props: {
  // TODO: why is template allowed to be undefined?
  template: { type: Object as PropType<HtmlTemplate | undefined>, required: false, default: undefined },
  templateLocale: { type: String as PropType<string | null | undefined>, required: false, default: undefined },
  saveBtnText: {
    type: [String, Object] as PropType<string|TranslateResult>, required: false, default: i18n.t('button.save'),
  },
},
  emits: ['close', 'save'],
  setup(__props, { emit }) {

const props = __props;

const services = ref([] as ReservationService[]);
const acctSettings = store.state.settings.account;





// image upload
const uploadDialog = ref(false);

let resolveUploadPromise: ((value: string) => void) | null = null;
let rejectUploadPromise: ((reason: any) => void) | null = null;

const onUpload = () => {
  uploadDialog.value = true;
  return new Promise<string>((resolve, reject) => {
    resolveUploadPromise = resolve;
    rejectUploadPromise = reject;
  });
};

const uploadImage = async (image: File | null) => {
  const ok = await performSaveAction(
    null,
    async () => {
      let imageUrl = '';
      // save image and get url, if undefined, image is being removed
      if (image !== null) {
        const key = store.state.settings.account.serviceManagerAccessKey ?? '';
        imageUrl = await httpClient.uploadImageFile(image, key);
      }

      resolveUploadPromise!(imageUrl);
    },
  );

  if (ok) {
    uploadDialog.value = false;
  } else {
    rejectUploadPromise!(new Error());
  }
};

const languages = useLanguages();

// Email Editor Callbacks
let toJsonExport = undefined as (() => string) | undefined;
let toHtmlExport = undefined as (() => string) | undefined;
let toHtmlPreview = undefined as (() => string) | undefined;
let toPlaintext = undefined as (() => string) | undefined;

const emailEditorCallbacks = {
  onUpload,
  toJsonExport: (cb: () => string) => { toJsonExport = cb; },
  toHtmlExport: (cb: () => string) => { toHtmlExport = cb; },
  toHtmlPreview: (cb: () => string) => { toHtmlPreview = cb; },
  toPlaintext: (cb: () => string) => { toPlaintext = cb; },
};

// /

const widgetLink = (s?: ReservationService) => {
  const { defaultAccessKey: apiKey } = store.state.settings.account;
  if (!apiKey) return undefined;
  if (!s) return hostedWidgetURL({ apiKey });

  const { locale, code: serviceCode } = s;
  if (!serviceCode) return undefined;
  return hostedWidgetURL({ apiKey, serviceCode, locale });
};

const reset = async () => {
  services.value = store.getters.serviceManagertServices as ReservationService[];

  const svcData = services.value.map((s) => ({
    id: s.code!,
    title: s.title,
    description: s.description,
    imageUrl: s.imageUrl,
    isActive: s.isActive,
    bookingLink: widgetLink(s) ?? '', // TODO: can this happen?
    locale: languages.value.find((l) => l.locale === s.locale)?.language,
  }));
  // console.log(svcData);

  const emailEditorData = {
    services: svcData,
    widgetLink: widgetLink() ?? '', // TODO: can this happen?
    logoUrl: acctSettings.logoLink,
    contactDetails: {
      name: acctSettings.accountName ?? '',
      address: `${acctSettings.streetAddress} ${acctSettings.postalCode} ${acctSettings.countryCode}`,
      phone: acctSettings.phone,
      email: acctSettings.email,
      website: acctSettings.website,
      socials: {
        facebook: acctSettings.facebook,
        instagram: acctSettings.instagram,
        yelp: acctSettings.yelp,
        x: acctSettings.x,
        youtube: acctSettings.youtube,
        tripadvisor: acctSettings.tripadvisor,
        tiktok: acctSettings.tiktok,
      },
    },
  };

  const lang = i18n.locale.split('-')[0];
  const documentLang = props.templateLocale ?? store.state.settings.account.locale ?? lang;
  const devMode = process.env.NODE_ENV !== 'production';
  InitApp(
    document.getElementById('email-editor-root')!,
    emailEditorData,
    emailEditorCallbacks,
    lang,
    documentLang,
    devMode,
    JSON.parse(props.template?.design ?? '{}'),
  );
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

const save = async () => {
  const design = toJsonExport!();
  console.log(design);
  const html = toHtmlExport!();
  console.log(html);
  const plaintext = toPlaintext!();
  console.log(plaintext);
  const preview = toHtmlPreview!();
  console.log(preview);

  let template: HtmlTemplate;
  if (props.template) {
    template = cloneModel(props.template);
    // template.name = 'edited name'
    template.design = design;
    template.html = html;
    template.plaintext = plaintext;
    template.preview = preview;
  } else {
    template = new HtmlTemplate({
      id: 0,
      // name = 'edited name',
      name: i18n.t('label.new_template') as string,
      design,
      html,
      plaintext,
      preview,
    });
  }

  emit('save', template);
};

const cancel = () => emit('close');

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

onBeforeUnmount(() => {
  DestroyApp();

  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    // no reset, reset perfomed when unloading marketing campaign manager store
    async () => {}, // store.dispatch('resetServiceManager');
  );
});


return { __sfc: true,services, acctSettings, props, emit, uploadDialog, resolveUploadPromise, rejectUploadPromise, onUpload, uploadImage, languages, toJsonExport, toHtmlExport, toHtmlPreview, toPlaintext, emailEditorCallbacks, widgetLink, reset, load, save, cancel, NewModalDialog, ImageUploadDialog }
}

})